// apps/web/src/pages/planejamento/central-conflitos-panel.tsx — Fase 03, Plano 07 (OPS-05, D-01).
import React, { useState } from 'react';
import {
  X,
  AlertOctagon,
  GitBranch,
  UsersRound,
  CalendarX,
  Gauge,
  CheckCircle2,
  ExternalLink,
  Shuffle,
} from 'lucide-react';
import type { ConflitoDetectado, ConflitosSnapshotResponse } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';

type TipoGrupoConflito = 'movimento_concorrente' | 'sobrecarga' | 'precedencia' | 'dupla_alocacao' | 'prazo';

interface CentralConflitosPanelProps {
  conflitos: ConflitosSnapshotResponse | null;
  loading: boolean;
  onClose: () => void;
  onNavigateToOs?: (osId: string) => void;
  onRecarregar?: () => void | Promise<void>;
}

const iconesPorTipo: Record<TipoGrupoConflito, React.ReactNode> = {
  movimento_concorrente: <Shuffle className="w-4 h-4 text-rose-600 shrink-0" />,
  sobrecarga: <Gauge className="w-4 h-4 text-rose-600 shrink-0" />,
  precedencia: <GitBranch className="w-4 h-4 text-amber-600 shrink-0" />,
  dupla_alocacao: <UsersRound className="w-4 h-4 text-purple-600 shrink-0" />,
  prazo: <CalendarX className="w-4 h-4 text-red-600 shrink-0" />,
};

const titulosPorTipo: Record<TipoGrupoConflito, string> = {
  movimento_concorrente: 'Movimento concorrente',
  sobrecarga: 'Sobrecarga de Capacidade',
  precedencia: 'Violação de Precedência',
  dupla_alocacao: 'Dupla Alocação de Recurso',
  prazo: 'Prazo Estourado',
};

export const CentralConflitosPanel: React.FC<CentralConflitosPanelProps> = ({
  conflitos,
  loading,
  onClose,
  onNavigateToOs,
  onRecarregar,
}) => {
  const total = conflitos?.total ?? 0;
  const [erroResolver, setErroResolver] = useState<{ osId: string; mensagem: string } | null>(null);
  const [resolvendoOsId, setResolvendoOsId] = useState<string | null>(null);

  async function resolver(osId: string): Promise<void> {
    setErroResolver(null);
    setResolvendoOsId(osId);
    try {
      const resposta = await apiMutate(`/api/v1/kanban/cartoes/${osId}/conflito/resolver`, { method: 'POST' });
      if (resposta.status === 403) {
        setErroResolver({ osId, mensagem: 'Sem permissão para resolver conflitos do Kanban.' });
        return;
      }
      if (!resposta.ok) {
        setErroResolver({ osId, mensagem: 'Não foi possível resolver o conflito do Kanban.' });
        return;
      }
      await onRecarregar?.();
    } catch {
      setErroResolver({ osId, mensagem: 'Não foi possível resolver o conflito do Kanban.' });
    } finally {
      setResolvendoOsId(null);
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl border-l z-50 flex flex-col animate-in slide-in-from-right duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-slate-50">
        <div className="flex items-center gap-2">
          <AlertOctagon className="w-5 h-5 text-amber-600" />
          <div>
            <h2 className="text-sm font-bold text-slate-900">Central de Conflitos</h2>
            <p className="text-[11px] text-slate-500 font-mono">
              {total} {total === 1 ? 'conflito identificado' : 'conflitos identificados'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200"
          aria-label="Fechar painel de conflitos"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : !conflitos || total === 0 ? (
          <div className="p-8 text-center space-y-2 border border-dashed rounded-xl bg-emerald-50/50">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
            <p className="font-semibold text-emerald-900">Nenhum conflito detectado.</p>
            <p className="text-[11px] text-emerald-700">
              O cronograma está dentro da capacidade, com precedências respeitadas e sem sobreposição de recursos.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Lista por Grupos de Tipo */}
            {(['movimento_concorrente', 'precedencia', 'dupla_alocacao', 'sobrecarga', 'prazo'] as TipoGrupoConflito[]).map((tipo) => {
              const lista = conflitos.agrupadoPorTipo[tipo] ?? [];
              if (lista.length === 0) return null;

              return (
                <div key={tipo} className="space-y-2">
                  <div className="flex items-center gap-1.5 font-bold text-slate-700 uppercase tracking-wider text-[11px]">
                    {iconesPorTipo[tipo]}
                    <span>{titulosPorTipo[tipo]} ({lista.length})</span>
                  </div>

                  <div className="space-y-2">
                    {lista.map((item: ConflitoDetectado) => (
                      <div
                        key={item.id}
                        className="p-3 bg-slate-50 border rounded-lg space-y-1.5 hover:bg-slate-100/80 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-semibold text-slate-900 truncate">
                            {item.titulo ?? item.mensagem}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                              item.severidade === 'critico'
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {item.severidade ?? 'alerta'}
                          </span>
                        </div>

                        <p className="text-slate-600 text-[11px] leading-relaxed">
                          {item.descricao ?? item.mensagem}
                        </p>

                        {item.osId && (
                          <div className="pt-1 flex items-center justify-end gap-3">
                            {tipo === 'movimento_concorrente' && (
                              <button
                                type="button"
                                disabled={resolvendoOsId === item.osId}
                                onClick={() => {
                                  const osId = item.osId;
                                  if (osId) void resolver(osId);
                                }}
                                className="inline-flex items-center text-[11px] font-semibold text-rose-700 hover:text-rose-900 hover:underline disabled:opacity-50"
                              >
                                Resolver
                              </button>
                            )}
                            <button
                              onClick={() => {
                                onNavigateToOs?.(item.osId!);
                                onClose();
                              }}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-slate-900 hover:underline"
                            >
                              <span>Ir para OS</span>
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {erroResolver && erroResolver.osId === item.osId && (
                          <p className="text-[11px] text-rose-700">{erroResolver.mensagem}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rodapé Informativo (Regra D-01: Resolução Manual) */}
      <div className="p-3 border-t bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
        <span>Resolução manual pelo usuário (D-01)</span>
        <span className="font-mono text-slate-400">Pieltec Gantt</span>
      </div>
    </div>
  );
};
